import Cookies from "js-cookie";
import { useEffect, useState } from "react";
import { Badge, Button, Card, Col, Row, Table } from "react-bootstrap";
import {
  FaBuilding,
  FaCoins,
  FaFileInvoiceDollar,
  FaUserPlus,
  FaUsers,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import franchiseAdminAPI from "../../api/franchiseAdminAPI";
import frymesOrderApi from "../../api/frymeOrderApi";
import usersApi from "../../api/userApi";

const Dashboard = () => {
  const [totalFranchises, setTotalFranchises] = useState(0);
  const email = Cookies.get("userEmail");
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customerCount, setCustomerCount] = useState(0);
  const [totalBoughtFrymes, setTotalBoughtFrymes] = useState(0);
  const [totalSoldFrymes, setTotalSoldFrymes] = useState(0);
  const [recentActivities, setRecentActivities] = useState([]);
  const navigate = useNavigate();
  const referralCode = Cookies.get("referralCode");
  useEffect(() => {
    const email = Cookies.get("userEmail");
    if (!email) {
      setError("User email not found in cookies");
      setLoading(false);
      return;
    }
    franchiseAdminAPI
      .getFranchiseDetailsByEmail(email)
      .then((res) => {
        setDetails(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to fetch franchise details");
        setLoading(false);
      });
  }, []);
  useEffect(() => {
    if (email) {
      fetchFranchises();
      fetchCustomerCount();
      fetchRecent();
      fetchTotalBoughtFrymes();
      fetchTotalSoldFrymes();
    }
  }, [email]);

  const fetchFranchises = async () => {
    try {
      const res = await franchiseAdminAPI.getCountOfFranchisesByEmail(email);
      // API may return plain number or { data: number }
      const count = typeof res === "number" ? res : (res?.data ?? 0);
      setTotalFranchises(count);
    } catch (e) {
      console.error("Failed to fetch franchise count", e);
      toast.error("Failed to load franchise count");
    }
  };

  const fetchCustomerCount = async () => {
    try {
      const res =
        await usersApi.getCountOfCustomersByReferralCode(details?.referralCode || referralCode);
      // API may return plain number or { data: number }
      const count = typeof res === "number" ? res : (res?.data ?? 0);
      setCustomerCount(count);
    } catch (e) {
      console.error("Failed to fetch customer count", e);
      toast.error("Failed to load customer count");
    }
  };

  const fetchTotalBoughtFrymes = async () => {
    try {
      const res = await frymesOrderApi.getTotalFrymesBoughtByFranchise(email);
      const count = typeof res === "number" ? res : (res?.data ?? 0);
      setTotalBoughtFrymes(count);
    } catch (e) {
      console.error("Failed to fetch total bought frymes", e);
      toast.error("Failed to load total bought frymes");
    }
  };

  const fetchTotalSoldFrymes = async () => {
    try {
      const res = await frymesOrderApi.getTotalFrymesSoldByFranchise(email);
      const count = typeof res === "number" ? res : (res?.data ?? 0);
      setTotalSoldFrymes(count);
    } catch (e) {
      console.error("Failed to fetch total sold frymes", e);
      toast.error("Failed to load total sold frymes");
    }
  };

  const fetchRecent = async () => {
    try {
      const res = await frymesOrderApi.getLast3RequestByFranchise(email);
      // Expected shape: { data: { content: [] } } or plain array
      const list = Array.isArray(res)
        ? res
        : (res?.data?.content ?? res?.data ?? []);
      setRecentActivities(list);
    } catch (e) {
      console.error("Failed to fetch recent requests", e);
      toast.error("Failed to load recent activity");
    }
  };

  return (
    <div fluid className="p-4 scrollable-container" style={{ height: "100%" }}>
      <h2 className="mb-4 fw-bold">Franchise Admin Dashboard</h2>

      {/* KPI Cards */}
      <Row className="g-4 mb-5">
        <Col md={3}>
          <Card className="text-center shadow-sm border-0 h-100">
            <Card.Body className="d-flex flex-column align-items-center justify-content-center">
              <FaBuilding size={40} className="text-primary mb-3" />
              <Card.Title className="text-muted">Total Franchises</Card.Title>
              <Card.Text className="display-5 fw-bold">
                {totalFranchises}
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center shadow-sm border-0 h-100">
            <Card.Body className="d-flex flex-column align-items-center justify-content-center">
              <FaUsers size={40} className="text-primary mb-3" />
              <Card.Title className="text-muted">Total Customers</Card.Title>
              <Card.Text className="display-5 fw-bold">
                {customerCount}
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center shadow-sm border-0 h-100">
            <Card.Body>
              <FaCoins size={40} className="text-success mb-3" />
              <Card.Title className="text-muted">Bought Frymes</Card.Title>
              <Card.Text className="display-5 fw-bold">
                {totalBoughtFrymes}
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="text-center shadow-sm border-0 h-100">
            <Card.Body>
              <FaCoins size={40} className="text-warning mb-3" />
              <Card.Title className="text-muted">Sold Frymes</Card.Title>
              <Card.Text className="display-5 fw-bold">
                {totalSoldFrymes}
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-4">
        {/* Recent Customers Table */}
        <Col md={8}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Header className="bg-white border-0 pt-4 pb-0">
              <h5 className="fw-bold mb-0">Recent Customers</h5>
            </Card.Header>
            <Card.Body>
              <Table responsive hover className="mt-3">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Activity</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentActivities.map((req, idx) => (
                    <tr key={idx}>
                      <td>{req.createdAt}</td>
                      <td>
                        {req.buyer ? (
                          <>
                            <strong>Franchise</strong>
                            <br />
                            <small>{req.buyer?.organization || "-"}</small>
                            <br />
                            <small>{req.buyer?.location || "-"}</small>
                            <br />
                            <small>{req.buyer?.email || "-"}</small>
                          </>
                        ) : (
                          <>
                            <strong>Customer</strong>
                            <br />
                            <small>{req.customerName || "Customer"}</small>
                            <br />

                            {/* Show company details if available */}
                            {req.companyDetails ? (
                              <>
                                <small>
                                  {req.companyDetails?.companyName || "-"}
                                </small>
                                <br />
                                <small>
                                  {req.companyDetails?.location || "-"}
                                </small>
                              </>
                            ) : (
                              <>
                                <small>{req.customerEmail || "-"}</small>
                                <small>{req.quantity || "-"}</small>
                              </>
                            )}
                          </>
                        )}
                      </td>

                      <td>
                        <Badge
                          bg={
                            req.status === "APPROVED"
                              ? "success"
                              : req.status === "REJECTED"
                                ? "danger"
                                : "warning"
                          }
                        >
                          {req.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                  {recentActivities.length === 0 && (
                    <tr>
                      <td colSpan={3} className="text-center">
                        No recent activity
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        {/* Quick Actions */}
        <Col md={4}>
          <Card className="shadow-sm border-0 h-100 bg-light">
            <Card.Header className="bg-transparent border-0 pt-4 pb-0">
              <h5 className="fw-bold mb-0">Quick Actions</h5>
            </Card.Header>
            <Card.Body className="d-flex flex-column gap-3 mt-3">
              <Button
                variant="outline-info"
                size="lg"
                className="d-flex align-items-center justify-content-center gap-2"
                onClick={() => navigate("/franchise-dashboard/franchises")}
              >
                <FaFileInvoiceDollar /> Add new Franchises
              </Button>
              <Button
                variant="primary"
                size="lg"
                className="d-flex align-items-center justify-content-center gap-2"
                onClick={() => navigate("/franchise-dashboard/customers")}
              >
                <FaUserPlus /> Invite New Customer
              </Button>
              <Button
                variant="outline-success"
                size="lg"
                className="d-flex align-items-center justify-content-center gap-2"
                onClick={() => navigate("/franchise-dashboard/frymes?tab=Buy")}
              >
                <FaCoins /> Request More Frymes
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
